import { useMemo } from 'react';

/**
 * Advanced custom hook to calculate financial summaries from a voucher ledger list.
 * Handles Sales, Purchases, Payments, Receipts, Debit/Credit Notes, Contras, and Journals.
 * @param {Object} props
 * @param {Array} props.voucherList - Array of voucher objects
 */
export const useVoucherSummary = ({ voucherList = [] }) => {
  return useMemo(() => {
    // Initializing tracking schema
    const initialSummary = {
      // Primary Sales & Purchases
      totalSale: 0,
      totalTaxableSale: 0,
      totalPurchase: 0,
      totalTaxablePurchase: 0,

      // Cash / Bank Movements
      totalPaymentOut: 0, // Explicit 'Payment' types
      totalPaymentIn: 0,  // Explicit 'Receipt' types

      // Tax Aggregations
      totalTaxInput: 0,   // GST on Purchases
      totalTaxOutput: 0,  // GST on Sales

      // New Standalone voucher breakdowns requested
      totalContra: 0,
      totalJournal: 0,
      totalCreditNote: 0,
      totalTaxableCreditNote: 0,
      totalCreditNoteTax: 0, // Tax on Credit Note
      totalDebitNote: 0,
      totalTaxableDebitNote: 0,
      totalDebitNoteTax: 0,  // Tax on Debit Note
    };

    if (!Array.isArray(voucherList) || voucherList.length === 0) {
      return initialSummary;
    }

    return voucherList.reduce((acc, voucher) => {
      // Parse numerical values securely from strings
      const grandTotal = parseFloat(voucher.grandTotal) || 0;
      const taxableTotal = parseFloat(voucher.taxableTotal) || 0;
      const gstTotal = parseFloat(voucher.gstTotal) || 0;

      switch (voucher.voucherType) {
        case 'Sale':
          acc.totalSale += grandTotal;
          acc.totalTaxableSale += taxableTotal;
          acc.totalTaxOutput += gstTotal;
          break;

        case 'Purchase':
          acc.totalPurchase += grandTotal;
          acc.totalTaxablePurchase += taxableTotal;
          acc.totalTaxInput += gstTotal;
          break;

        case 'Payment':
          acc.totalPaymentOut += grandTotal;
          break;

        case 'Receipt':
          acc.totalPaymentIn += grandTotal;
          break;

        case 'CreditNote':
          acc.totalCreditNote += grandTotal;
          acc.totalTaxableCreditNote += taxableTotal;
          acc.totalCreditNoteTax += gstTotal;
          // Accounting choice: Often reduces overall Tax Output
          acc.totalTaxOutput -= gstTotal; 
          break;

        case 'DebitNote':
          acc.totalDebitNote += grandTotal;
          acc.totalTaxableDebitNote += taxableTotal;
          acc.totalDebitNoteTax += gstTotal;
          // Accounting choice: Often reduces overall Tax Input
          acc.totalTaxInput -= gstTotal; 
          break;

        case 'Contra':
          acc.totalContra += grandTotal;
          // Contra is money moving internally (e.g., Bank to Cash). 
          // It counts as both cash outgoing from source and incoming to destination.
          //acc.totalPaymentOut += grandTotal;
          //acc.totalPaymentIn += grandTotal;
          break;

        case 'Journal':
          acc.totalJournal += grandTotal;
          // Adjustments might influence payment flows depending on context.
          // If your JV system handles physical payments, uncomment below:
          // acc.totalPaymentOut += grandTotal;
          // acc.totalPaymentIn += grandTotal;
          break;

        default:
          // 'Expense', 'ChallanOut', 'ChallanIn' skip calculation loops safely
          break;
      }

      return acc;
    }, initialSummary);
  }, [voucherList]);
};